import { createClient } from '@supabase/supabase-js'

const SUPABASE_URL = 'https://vphlmqcrjizifjhlldzr.supabase.co'
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZwaGxtcWNyaml6aWZqaGxsZHpyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTg3NDM2NTcsImV4cCI6MjA3NDMxOTY1N30.Y1Qh3CQMVmD91NiUYQ8eP_AxHWSPJBbhetZYQwdtyds'

if (SUPABASE_URL === 'https://<PROJECT-ID>.supabase.co' || SUPABASE_ANON_KEY === '<ANON_KEY>') {
  throw new Error('Missing Supabase variables');
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true
  }
})

// Password hashing utility functions
const passwordUtils = {
  // Simple hash function for demo (in production use bcrypt or similar)
  async hashPassword(password) {
    const encoder = new TextEncoder();
    const data = encoder.encode(password + 'HomeScout2025'); // Salt
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  },

  async verifyPassword(password, hash) {
    const hashedInput = await this.hashPassword(password);
    return hashedInput === hash;
  }
};

// Database helper functions
export const supabaseHelpers = {
  // Auth helpers
  async signUp(email, password, userData) {
    try {
      console.log('Creating user with data:', { email, ...userData });
      
      // Validate password
      if (!password || password.length < 6) {
        return { data: null, error: { message: 'La password deve essere di almeno 6 caratteri' } };
      }
      
      // Check if user already exists
      const { data: existingUsers, error: checkError } = await supabase
        .from('users_hs2025')
        .select('email')
        .eq('email', email);
      
      if (checkError) {
        console.error('Error checking existing users:', checkError);
        return { data: null, error: checkError };
      }
      
      if (existingUsers && existingUsers.length > 0) {
        console.log('User already exists:', email);
        return { data: null, error: { message: 'Email già registrata' } };
      }

      // Hash password
      const passwordHash = await passwordUtils.hashPassword(password);
      console.log('Password hashed successfully');

      // Create user profile in our custom table
      const userProfile = {
        id: crypto.randomUUID(),
        email,
        password_hash: passwordHash, // Store hashed password
        phone_number: userData.phone_number,
        role: userData.role,
        created_at: new Date().toISOString()
      }

      console.log('Inserting user profile:', { ...userProfile, password_hash: '[HIDDEN]' });

      const { data, error } = await supabase
        .from('users_hs2025')
        .insert([userProfile])
        .select('id, email, phone_number, role, created_at') // Don't return password hash
        .single()

      if (error) {
        console.error('Error creating user profile:', error);
        return { data: null, error }
      }

      console.log('User created successfully:', data);
      return { data: { user: data }, error: null }
    } catch (err) {
      console.error('Signup error:', err);
      return { data: null, error: err }
    }
  },

  async signIn(email, password) {
    try {
      console.log('Attempting sign in for:', email);
      
      // Validate input
      if (!email || !password) {
        return { data: null, error: { message: 'Email e password sono obbligatori' } };
      }
      
      // Get user with password hash
      const { data, error } = await supabase
        .from('users_hs2025')
        .select('id, email, phone_number, role, created_at, password_hash')
        .eq('email', email)
        .single()

      if (error || !data) {
        console.log('Login failed - user not found:', error);
        return { data: null, error: { message: 'Email o password non validi' } }
      }

      // Verify password
      const passwordValid = await passwordUtils.verifyPassword(password, data.password_hash);
      
      if (!passwordValid) {
        console.log('Login failed - invalid password');
        return { data: null, error: { message: 'Email o password non validi' } }
      }

      // Remove password hash from response
      const { password_hash, ...userWithoutPassword } = data;
      
      console.log('Login successful for user:', userWithoutPassword);
      return { data: { user: userWithoutPassword }, error: null }
    } catch (err) {
      console.error('Signin error:', err);
      return { data: null, error: { message: 'Errore durante il login' } }
    }
  },

  async signOut() {
    // For our custom auth, we just clear the local state
    return { error: null }
  },

  async getCurrentUser() {
    // This will be handled by the useAuth hook
    return null
  },

  // Password change functionality
  async changePassword(userId, currentPassword, newPassword) {
    try {
      console.log('Changing password for user:', userId);
      
      // Validate new password
      if (!newPassword || newPassword.length < 6) {
        return { data: null, error: { message: 'La nuova password deve essere di almeno 6 caratteri' } };
      }
      
      // Get current user data
      const { data: userData, error: fetchError } = await supabase
        .from('users_hs2025')
        .select('password_hash')
        .eq('id', userId)
        .single();
      
      if (fetchError || !userData) {
        return { data: null, error: { message: 'Utente non trovato' } };
      }
      
      // Verify current password
      const currentPasswordValid = await passwordUtils.verifyPassword(currentPassword, userData.password_hash);
      
      if (!currentPasswordValid) {
        return { data: null, error: { message: 'Password attuale non corretta' } };
      }
      
      // Hash new password
      const newPasswordHash = await passwordUtils.hashPassword(newPassword);
      
      // Update password
      const { error: updateError } = await supabase
        .from('users_hs2025')
        .update({ password_hash: newPasswordHash })
        .eq('id', userId);
      
      if (updateError) {
        return { data: null, error: updateError };
      }
      
      console.log('Password changed successfully');
      return { data: { success: true }, error: null };
    } catch (err) {
      console.error('Change password error:', err);
      return { data: null, error: err };
    }
  },

  // Users helpers
  async getUserProfile(userId) {
    console.log('Getting user profile for:', userId);
    
    const { data, error } = await supabase
      .from('users_hs2025')
      .select('id, email, phone_number, role, created_at') // Don't select password_hash
      .eq('id', userId)
      .single()

    console.log('User profile result:', { data, error });
    return { data, error }
  },

  async updateUserProfile(userId, updates) {
    console.log('Updating user profile:', userId, updates);
    
    // Remove sensitive fields that shouldn't be updated this way
    const { password_hash, ...safeUpdates } = updates;
    
    const { data, error } = await supabase
      .from('users_hs2025')
      .update(safeUpdates)
      .eq('id', userId)
      .select('id, email, phone_number, role, created_at')
      .single()

    console.log('Update result:', { data, error });
    return { data, error }
  },

  async getAllUsers() {
    console.log('Fetching all users...');
    
    const { data, error } = await supabase
      .from('users_hs2025')
      .select('id, email, phone_number, role, created_at') // Don't select password_hash
      .order('created_at', { ascending: false })

    console.log('All users result:', { 
      count: data?.length || 0, 
      users: data?.map(u => ({ id: u.id, email: u.email, role: u.role })),
      error 
    });
    
    return { data: data || [], error }
  },

  async deleteUser(userId) {
    console.log('Deleting user:', userId);
    
    // First delete all segnalazioni for this user
    const { error: segnalazioniError } = await supabase
      .from('segnalazioni_hs2025')
      .delete()
      .eq('user_id', userId);

    if (segnalazioniError) {
      console.error('Error deleting user segnalazioni:', segnalazioniError);
      return { error: segnalazioniError };
    }

    // Then delete the user
    const { error } = await supabase
      .from('users_hs2025')
      .delete()
      .eq('id', userId)

    console.log('Delete user result:', { error });
    return { error }
  },

  // Segnalazioni helpers (unchanged)
  async createSegnalazione(segnalazioneData) {
    try {
      console.log('Creating segnalazione with data:', segnalazioneData)
      
      // Prepara i dati per il salvataggio
      const dataToSave = {
        id: segnalazioneData.id || crypto.randomUUID(),
        user_id: segnalazioneData.user_id,
        indirizzo: segnalazioneData.indirizzo,
        mq: parseInt(segnalazioneData.mq),
        nome_proprietario: segnalazioneData.nome_proprietario,
        telefono_proprietario: segnalazioneData.telefono_proprietario,
        tipo_immobile: segnalazioneData.tipo_immobile,
        stato_immobile: segnalazioneData.stato_immobile,
        stato_segnalazione: segnalazioneData.stato_segnalazione || 'In attesa',
        note: segnalazioneData.note || null,
        note_admin: segnalazioneData.note_admin || null,
        images: JSON.stringify(segnalazioneData.images || []), // Converti in stringa JSON
        created_at: new Date().toISOString()
      }

      console.log('Data to save:', dataToSave)

      const { data, error } = await supabase
        .from('segnalazioni_hs2025')
        .insert([dataToSave])
        .select()
        .single()

      if (error) {
        console.error('Supabase error:', error)
        return { data: null, error }
      }

      // Riconverti le immagini da stringa JSON a array
      if (data && data.images) {
        try {
          data.images = JSON.parse(data.images)
        } catch (e) {
          console.warn('Error parsing images JSON:', e)
          data.images = []
        }
      }

      console.log('Segnalazione created successfully:', data)
      return { data, error: null }
    } catch (err) {
      console.error('Error in createSegnalazione:', err)
      return { data: null, error: err }
    }
  },

  async getAllSegnalazioni() {
    try {
      console.log('Fetching all segnalazioni...');
      
      const { data, error } = await supabase
        .from('segnalazioni_hs2025')
        .select(`
          *,
          users_hs2025 (
            id,
            email,
            phone_number,
            role
          )
        `)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching segnalazioni:', error)
        return { data: null, error }
      }

      console.log('Fetched segnalazioni:', {
        count: data?.length || 0,
        segnalazioni: data?.map(s => ({ id: s.id, indirizzo: s.indirizzo, user_id: s.user_id }))
      });

      // Converte le immagini da stringa JSON ad array per ogni segnalazione
      const processedData = data?.map(segnalazione => ({
        ...segnalazione,
        images: segnalazione.images ? 
          (typeof segnalazione.images === 'string' ? 
            JSON.parse(segnalazione.images) : 
            segnalazione.images
          ) : []
      })) || []

      return { data: processedData, error: null }
    } catch (err) {
      console.error('Error in getAllSegnalazioni:', err)
      return { data: [], error: err }
    }
  },

  async getSegnalazioniByUser(userId) {
    try {
      console.log('Fetching segnalazioni for user:', userId);
      
      const { data, error } = await supabase
        .from('segnalazioni_hs2025')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching user segnalazioni:', error)
        return { data: null, error }
      }

      console.log('User segnalazioni:', {
        userId,
        count: data?.length || 0
      });

      // Converte le immagini da stringa JSON ad array per ogni segnalazione
      const processedData = data?.map(segnalazione => ({
        ...segnalazione,
        images: segnalazione.images ? 
          (typeof segnalazione.images === 'string' ? 
            JSON.parse(segnalazione.images) : 
            segnalazione.images
          ) : []
      })) || []

      return { data: processedData, error: null }
    } catch (err) {
      console.error('Error in getSegnalazioniByUser:', err)
      return { data: [], error: err }
    }
  },

  async updateSegnalazione(segnalazioneId, updates) {
    try {
      console.log('Updating segnalazione:', segnalazioneId, updates)
      
      // IMPORTANTE: Prepara i dati per l'aggiornamento SENZA modificare user_id
      let dataToUpdate = { ...updates }
      
      // RIMUOVI user_id dagli aggiornamenti per preservare il proprietario originale
      // Questo garantisce che l'admin non possa cambiare il proprietario della segnalazione
      delete dataToUpdate.user_id
      
      // Converti le immagini in stringa JSON se presenti
      if (updates.images) {
        dataToUpdate.images = JSON.stringify(updates.images)
      }

      // Converti mq in numero se presente
      if (updates.mq) {
        dataToUpdate.mq = parseInt(updates.mq)
      }

      console.log('Data to update (without user_id):', dataToUpdate)

      const { data, error } = await supabase
        .from('segnalazioni_hs2025')
        .update(dataToUpdate)
        .eq('id', segnalazioneId)
        .select(`
          *,
          users_hs2025 (
            id,
            email,
            phone_number,
            role
          )
        `)
        .single()

      if (error) {
        console.error('Supabase update error:', error)
        return { data: null, error }
      }

      // Riconverti le immagini da stringa JSON a array
      if (data && data.images) {
        try {
          data.images = JSON.parse(data.images)
        } catch (e) {
          console.warn('Error parsing images JSON:', e)
          data.images = []
        }
      }

      console.log('Segnalazione updated successfully (user_id preserved):', data)
      return { data, error: null }
    } catch (err) {
      console.error('Error in updateSegnalazione:', err)
      return { data: null, error: err }
    }
  },

  async deleteSegnalazione(segnalazioneId) {
    console.log('Deleting segnalazione:', segnalazioneId);
    
    const { error } = await supabase
      .from('segnalazioni_hs2025')
      .delete()
      .eq('id', segnalazioneId)

    console.log('Delete segnalazione result:', { error });
    return { error }
  },

  // Image upload helper - Migliorato per gestire meglio le immagini
  async uploadImage(file, bucket = 'segnalazioni-images') {
    try {
      console.log('Uploading image:', file.name, file.size)
      
      // Valida il file
      if (!file.type.startsWith('image/')) {
        throw new Error('Il file deve essere un\'immagine')
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        throw new Error('Il file è troppo grande (massimo 5MB)')
      }

      // Crea una rappresentazione base64 dell'immagine per la demo
      const reader = new FileReader()
      
      return new Promise((resolve) => {
        reader.onload = function(e) {
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${file.name.split('.').pop()}`
          
          console.log('Image processed successfully:', fileName)
          
          resolve({
            data: {
              path: fileName,
              url: e.target.result // Usa base64 per la demo
            },
            error: null
          })
        }
        
        reader.onerror = function() {
          resolve({
            data: null,
            error: new Error('Errore durante la lettura del file')
          })
        }
        
        reader.readAsDataURL(file)
      })
    } catch (err) {
      console.error('Error in uploadImage:', err)
      return { data: null, error: err }
    }
  },

  async deleteImage(fileName, bucket = 'segnalazioni-images') {
    // For now, just return success since we're using base64 images
    return { error: null }
  }
}